import { Input, OutlinedInput, OutlinedInputProps } from '@mui/material';
import React from 'react';
import characters from 'src/constants/characters';
import { ClickElementType } from 'src/lib/analytics/events';
import { Typography } from '../Typography';
import { Field, FieldProps } from './Field';
import withFormikConsumer from './withFormikConsumer';

type TextFieldVariant = 'outlined' | 'text';
export interface TextFieldProps
    extends Pick<
            FieldProps,
            'editable' | 'error' | 'helperText' | 'label' | 'labelAdornment' | 'inlineLabelAdornment' | 'tooltip' | 'wide' | 'required'
        >,
        Pick<
            OutlinedInputProps,
            | 'autoComplete'
            | 'autoFocus'
            | 'defaultValue'
            | 'disabled'
            | 'multiline'
            | 'name'
            | 'placeholder'
            | 'rows'
            | 'maxRows'
            | 'type'
            | 'onChange'
            | 'value'
            | 'size'
            | 'startAdornment'
            | 'endAdornment'
            | 'inputProps'
        > {
    trimWhiteSpace?: boolean;
    variant?: TextFieldVariant;
    emptyNode?: React.ReactNode;
}

export const Text = ({
    editable = true,
    error,
    helperText,
    label,
    labelAdornment,
    inlineLabelAdornment,
    tooltip,
    value,
    onChange,
    trimWhiteSpace = false,
    wide = false,
    required = false,
    variant = 'outlined',
    emptyNode = characters.emdash,
    ...inputProps
}: TextFieldProps) => {
    return (
        <Field
            editable={editable}
            error={error}
            label={label}
            labelAdornment={labelAdornment}
            inlineLabelAdornment={inlineLabelAdornment}
            tooltip={tooltip}
            helperText={helperText}
            wide={wide}
            required={required}
            elementType={ClickElementType.TEXT_INPUT}
            name={inputProps.name}
        >
            {!editable ? (
                <Typography role="body" sx={{ overflowWrap: 'break-word' }}>
                    {value ? String(value) : emptyNode}
                </Typography>
            ) : variant === 'text' ? (
                <Input
                    error={!!error}
                    fullWidth
                    inputProps={{
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            ) : (
                <OutlinedInput
                    sx={{
                        backgroundColor: (theme) => theme.resqPalette.surface.default,
                    }}
                    error={!!error}
                    fullWidth
                    inputProps={{
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            )}
        </Field>
    );
};
export const TextField = withFormikConsumer<
    NonNullable<OutlinedInputProps['value']>,
    TextFieldProps
>(Text);
